import {
  ref,
  uploadBytes,
  uploadString,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { storage, auth } from '../firebase';

export interface UploadResult {
  downloadUrl: string;
  storagePath: string;
  fileSizeBytes?: number;
}

class FirebaseStorageService {
  /**
   * Uploads base64 data URL (e.g. from camera/watermark canvas) directly to Firebase Storage
   */
  public async uploadPhotoDataUrl(
    accountId: string,
    photoId: string,
    dataUrl: string
  ): Promise<UploadResult> {
    try {
      const sanitizedAccId = (accountId || 'general').replace(/[^a-zA-Z0-9_-]/g, '_');
      const sanitizedPhotoId = (photoId || `photo_${Date.now()}`).replace(/[^a-zA-Z0-9_-]/g, '_');
      const storagePath = `photos/${sanitizedAccId}/${sanitizedPhotoId}_${Date.now()}.jpg`;
      const storageRef = ref(storage, storagePath);

      // Upload base64 data_url
      await uploadString(storageRef, dataUrl, 'data_url', {
        contentType: 'image/jpeg',
        customMetadata: {
          accountId: sanitizedAccId,
          photoId: sanitizedPhotoId,
          uploadedBy: auth.currentUser?.uid || 'unknown',
        },
      });

      const downloadUrl = await getDownloadURL(storageRef);
      return { downloadUrl, storagePath };
    } catch (err: any) {
      console.warn('Firebase Storage photo upload notice:', err?.message || err);
      // Fallback: preserve dataUrl if storage upload encountered an error so user never loses their photo
      return { downloadUrl: dataUrl, storagePath: '' };
    }
  }

  /**
   * Uploads an audio blob (voice note) directly to Firebase Storage
   */
  public async uploadAudioBlob(
    accountId: string,
    voiceNoteId: string,
    audioBlob: Blob | File
  ): Promise<UploadResult> {
    try {
      const sanitizedAccId = (accountId || 'general').replace(/[^a-zA-Z0-9_-]/g, '_');
      const sanitizedVnId = (voiceNoteId || `vn_${Date.now()}`).replace(/[^a-zA-Z0-9_-]/g, '_');
      const ext = audioBlob.type.includes('mp3') ? 'mp3' : 'webm';
      const storagePath = `voicenotes/${sanitizedAccId}/${sanitizedVnId}_${Date.now()}.${ext}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, audioBlob, {
        contentType: audioBlob.type || 'audio/webm',
        customMetadata: {
          accountId: sanitizedAccId,
          voiceNoteId: sanitizedVnId,
          uploadedBy: auth.currentUser?.uid || 'unknown',
        },
      });

      const downloadUrl = await getDownloadURL(storageRef);
      return {
        downloadUrl,
        storagePath,
        fileSizeBytes: audioBlob.size,
      };
    } catch (err: any) {
      console.warn('Firebase Storage audio upload notice:', err?.message || err);
      // Fallback to local Object URL if storage fails
      let fallbackUrl = '';
      try {
        fallbackUrl = URL.createObjectURL(audioBlob);
      } catch {}
      return { downloadUrl: fallbackUrl, storagePath: '', fileSizeBytes: audioBlob.size };
    }
  }

  /**
   * Uploads a document (PDF, image, KYC) directly to Firebase Storage
   */
  public async uploadDocumentFile(
    accountId: string,
    documentId: string,
    file: File | Blob,
    fileName: string
  ): Promise<UploadResult> {
    try {
      const sanitizedAccId = (accountId || 'general').replace(/[^a-zA-Z0-9_-]/g, '_');
      const sanitizedDocId = (documentId || `doc_${Date.now()}`).replace(/[^a-zA-Z0-9_-]/g, '_');
      const cleanFileName = fileName.replace(/[^a-zA-Z0-9_.-]/g, '_');
      const storagePath = `documents/${sanitizedAccId}/${sanitizedDocId}_${cleanFileName}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, file, {
        contentType: (file as File).type || 'application/octet-stream',
        customMetadata: {
          accountId: sanitizedAccId,
          documentId: sanitizedDocId,
          originalName: fileName,
          uploadedBy: auth.currentUser?.uid || 'unknown',
        },
      });

      const downloadUrl = await getDownloadURL(storageRef);
      return {
        downloadUrl,
        storagePath,
        fileSizeBytes: file.size,
      };
    } catch (err: any) {
      console.warn('Firebase Storage document upload notice:', err?.message || err);
      let fallbackUrl = '';
      try {
        fallbackUrl = URL.createObjectURL(file);
      } catch {}
      return { downloadUrl: fallbackUrl, storagePath: '', fileSizeBytes: file.size };
    }
  }

  /**
   * Remove a stored file (photo, voice note, document) by its storage path.
   *
   * A missing file is treated as success -- the caller's goal is that the
   * file is gone, and it already is.
   */
  public async deleteFile(storagePath: string): Promise<void> {
    if (!storagePath) return;
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete files.');
    }
    try {
      await deleteObject(ref(storage, storagePath));
    } catch (err: any) {
      if (err?.code === 'storage/object-not-found') return;
      throw err;
    }
  }
}

export const firebaseStorageService = new FirebaseStorageService();
